import { useMemo, useRef, useState } from 'react';

import { DropdownMenu2, dropdownMenu2Classes, Tooltip } from '@kc-console/common-ui';
import { useMegaMenuQuery, useScopedLinkBuilder } from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import IcQuestionCircleSmallRegular from '@kep/kcds-icons/IcQuestionCircleSmallRegular';

import GnbIconButton from './GnbIconButton';

import type { DropdownMenu2ItemData } from '@kc-console/common-ui';

const HELPDESK_SERVICE_KEYWORD = 'helpdesk';

const HelpdeskMenu = () => {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const { data: categories = [] } = useMegaMenuQuery();
  const { getProps } = useScopedLinkBuilder();

  const helpdeskMenus = useMemo(() => {
    const service = categories
      .flatMap(category => category.services)
      .find(service => service.keyword === HELPDESK_SERVICE_KEYWORD);
    return service?.menus ?? [];
  }, [categories]);

  const helpdeskMenuItems = useMemo(() => {
    const sectionItem: DropdownMenu2ItemData = {
      type: 'section',
      title: 'Help Desk',
      header: t('layout:gnb.helpdesk.need_help'),
    };
    const optionItems: DropdownMenu2ItemData[] = helpdeskMenus.map((menu, index) => ({
      type: 'option',
      text: menu.name,
      showDividerAbove: index === 0,
      // TODO(navigation): DropdownMenu2 anchor 지원 시 isAnchor:false 제거 → href spread
      onClick: getProps(menu.path, { external: true, isAnchor: false }).onClick,
    }));
    return [sectionItem, ...optionItems];
  }, [helpdeskMenus, t, getProps]);

  return (
    <>
      <Tooltip message={t('layout:gnb.helpdesk.title')}>
        <div ref={anchor}>
          <GnbIconButton
            disableRipple
            aria-expanded={open}
            aria-haspopup="menu"
            aria-label={t('layout:gnb.helpdesk.title')}
            onClick={() => setOpen(prev => !prev)}
          >
            <IcQuestionCircleSmallRegular />
          </GnbIconButton>
        </div>
      </Tooltip>
      <DropdownMenu2
        anchorEl={anchor.current}
        open={open}
        items={helpdeskMenuItems}
        onClose={() => setOpen(false)}
        css={{
          [`.${dropdownMenu2Classes.paper}`]: {
            width: 248,
          },
        }}
      />
    </>
  );
};

export default HelpdeskMenu;
